import { useEffect } from 'react';

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import * as emitterEvents from 'constants/emitterEvents';
import Emitter from 'services/eventEmitter';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  fetchAtendimentosIniciados,
  fetchFilaV2,
  fetchMarcarAtendimentoComoLido,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import {
  canaisSelector,
  fetchCanaisSelect,
  TipoCanalEnum,
} from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  addMensagemAtendimento,
  encaminharMensagens,
  mensagensSelector,
} from 'store/reducers/mensagens';
import { usuariosSelector, fetchMinhasEquipes } from 'store/reducers/usuarios';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { getUrlParams } from 'utils/getUrlParams';
import * as validators from 'utils/validators';

type ModalEncaminharMensagensProps = {
  open: boolean;
  contato?: ContatoInterface;
  onClose: () => void;
  onSuccess?: () => void;
  onChangeContatoSelected?: () => void;
};

const useStyles = makeStyles(({ spacing }) => ({
  textField: {
    marginTop: spacing(2),
  },
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    marginRight: spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}));

export default function ModalEncaminharMensagens(
  props: ModalEncaminharMensagensProps,
) {
  const { open, onClose, onSuccess, onChangeContatoSelected, contato } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { canaisSelect, loadingCanaisSelect } = useAppSelector(canaisSelector);
  const { minhasEquipes, loadingMinhasEquipes } =
    useAppSelector(usuariosSelector);
  const { mensagensParaEncaminhar } = useAppSelector(mensagensSelector);
  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const { orderBy, orderInitiatedBy } = getUrlParams();

  const isInstagramOrMessengerChannel =
    contato?.tipoCanal === TipoCanalEnum.INSTAGRAM ||
    contato?.tipoCanal === TipoCanalEnum.MESSENGER;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      canal: '',
      equipe: minhasEquipes.length === 1 ? minhasEquipes[0]?.id : '',
    },
    validationSchema: validators.iniciarContatoSchema,
    onSubmit: async values => {
      if (!contato) return;

      const data = {
        channelId: values.canal,
        teamId: values.equipe,
        contactIds: [contato.id],
        messageIds: mensagensParaEncaminhar,
      };

      /**
       * Processos cruciais realizados após encaminhar mensagens:
       *
       * 1º Atualiza a fila de espera
       * 2º Atualiza a lista de atendimentos iniciados
       * 3º Seleciona o atendimento iniciado para atendimento
       * 4º Confirma a leitura e zera o contador das mensagens nao lidas do atendimento iniciado
       */

      await dispatch(encaminharMensagens(data))
        .unwrap()
        .then(({ atendimento, messages }) => {
          dispatch(fetchFilaV2({ orderBy }));
          dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }))
            .unwrap()
            .then(novaFila => {
              const novoAtendimento = novaFila.find(
                item => item.id === atendimento?.id,
              );

              if (novoAtendimento) {
                if (novoAtendimento.id === atendimentoSelecionado?.id) {
                  messages.forEach(mensagem => {
                    dispatch(addMensagemAtendimento(mensagem));
                  });
                  sendMessageAtendenteNoScrollTop(true);
                } else {
                  dispatch(setAtendimentoSelecionado(novoAtendimento));
                }
              }
            });

          const atendimentoId = atendimento?.id;
          if (atendimentoId) {
            dispatch(fetchMarcarAtendimentoComoLido(atendimentoId));
          }

          if (onSuccess) onSuccess();

          onClose();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    handleChange,
    resetForm,
    touched,
    errors,
    isSubmitting,
  } = formik;

  const handleClose = () => {
    if (onClose && !isSubmitting) onClose();
  };

  const sendMessageAtendenteNoScrollTop = (value: boolean) => {
    Emitter.emit(
      emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
      value,
    );
  };

  // Auto seleciona a equipe caso tenha apenas uma
  useEffect(() => {
    if (open && contato && minhasEquipes.length === 1 && !values.equipe) {
      setFieldValue('equipe', minhasEquipes[0].id);
    }
  }, [open, contato, minhasEquipes, setFieldValue, values.equipe]);

  // pega os canais online pelo tipo sempre que seleciona um contato
  useEffect(() => {
    if (!open) return;

    const tipoCanal = contato?.tipoCanal;
    const contatoId = contato?.id;
    const equipeId = values.equipe;

    // Busca os canais pelo tipo do canal do contato selecionado
    // e passa o id do contato para filtrar os canais disponíveis
    if (tipoCanal && contatoId && equipeId) {
      const tiposCanaisWhatsapp = [
        TipoCanalEnum.WHATSAPP,
        TipoCanalEnum.WHATSAPP_OFICIAL,
      ];

      if (tiposCanaisWhatsapp.includes(tipoCanal as TipoCanalEnum)) {
        dispatch(
          fetchCanaisSelect({
            isActive: true,
            types: tiposCanaisWhatsapp,
            equipeId,
            contactId: contatoId,
          }),
        );
        return;
      }

      dispatch(
        fetchCanaisSelect({
          isActive: true,
          types: [tipoCanal],
          equipeId,
          contactId: contatoId,
        }),
      );
    }
  }, [contato?.id, contato?.tipoCanal, values.equipe, dispatch, open]);

  // Auto seleciona o canal caso tenha apenas um ou um canal padrão
  useEffect(() => {
    if (canaisSelect.length === 0) {
      setFieldValue('canal', '');
      return;
    }

    if (canaisSelect.length === 1) {
      setFieldValue('canal', canaisSelect[0].id);
    } else {
      const defaultCanal = canaisSelect.find(c => c.isDefault);
      if (defaultCanal) {
        setFieldValue('canal', defaultCanal.id);
      }
    }
  }, [canaisSelect, setFieldValue]);

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onEnter: () => dispatch(fetchMinhasEquipes()),
        onExited: () => resetForm(),
      }}
    >
      <DialogHeader
        title="Encaminhar mensagens"
        description="Agora selecione a equipe e o canal para encaminhar as mensagens."
        onClose={handleClose}
      />

      <Box ml={3} mr={3}>
        <ListItem
          button
          selected
          disabled={!onChangeContatoSelected}
          onClick={() => {
            if (onChangeContatoSelected) onChangeContatoSelected();
          }}
        >
          <ListItemAvatar>
            <Avatar src={contato?.foto} />
          </ListItemAvatar>

          <ListItemText
            primary={contato?.nome}
            secondary={
              isInstagramOrMessengerChannel
                ? `Canal: ${contato?.canal?.nome}`
                : contato?.contato
            }
            primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
            secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
          />
        </ListItem>
      </Box>

      <DialogForm onSubmit={handleSubmit} loading={isSubmitting}>
        <DialogContent>
          <TextField
            select
            fullWidth
            id="equipe"
            name="equipe"
            variant="outlined"
            label={
              minhasEquipes.length > 0 ? 'Equipe' : 'Nenhuma equipe disponível'
            }
            disabled={
              loadingMinhasEquipes || minhasEquipes.length <= 0 || !contato
            }
            className={classes.textField}
            value={values.equipe}
            onChange={e => {
              setFieldValue('canal', '');
              handleChange(e);
            }}
            error={touched.equipe && Boolean(errors.equipe)}
            helperText={touched.equipe && errors.equipe}
            InputProps={{
              startAdornment: loadingMinhasEquipes && (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ),
            }}
          >
            {minhasEquipes.map(equipe => (
              <MenuItem key={equipe.id} value={equipe.id}>
                <Typography variant="body2">{equipe.nome}</Typography>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            id="canal"
            name="canal"
            variant="outlined"
            label={
              canaisSelect.length > 0 ? 'Canal' : 'Nenhum canal disponível'
            }
            disabled={
              loadingCanaisSelect ||
              canaisSelect.length <= 0 ||
              isInstagramOrMessengerChannel ||
              !values.equipe
            }
            className={classes.textField}
            value={values.canal}
            onChange={handleChange}
            error={touched.canal && Boolean(errors.canal)}
            helperText={touched.canal && errors.canal}
            InputProps={{
              startAdornment: loadingCanaisSelect && (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ),
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: selected => {
                if ((selected as string) === '') {
                  return null;
                }
                const canal = canaisSelect.find(c => c.id === selected);
                return canal ? (
                  <Box className={classes.truncateValue}>
                    <span style={{ flexShrink: 0 }}>
                      {getIconTipoCanal({
                        tipoCanal: canal.type,
                        size: 16,
                        isModoHibrido: canal.isHybridMode,
                      })}
                    </span>
                    <span className={classes.truncateText}>{canal.name}</span>
                  </Box>
                ) : (
                  selected
                );
              },
            }}
          >
            {canaisSelect.map(canal => (
              <MenuItem
                key={canal.id}
                value={canal.id}
                className={classes.menuItem}
              >
                {getIconTipoCanal({
                  tipoCanal: canal.type,
                  size: 16,
                  isModoHibrido: canal.isHybridMode,
                })}
                <span style={{ paddingTop: 2 }}>{canal.name}</span>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button color="primary" variant="contained" type="submit">
            Enviar
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}
